import {parse, stringify} from "../../../service/json/json-comment-wrapper";
import {List} from "immutable";
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {withNamespaces, WithNamespaces} from "react-i18next";
import {useDispatch, useSelector} from "react-redux";
import {FormField, Message} from "semantic-ui-react";
import CodeEditor from "../../../../core/components/CodeEditor/CodeEditor";
import {makeEditorStateByNameSelector} from "../../../../Layout/selectors/sidebar-editor";
import {useElementsTree} from "../../../hooks/useElementsTree";
import {BoardId} from "../../../model/Board";
import {Node, NodeType} from "../../../model/Graph";
import {ImmutablePolicyContext} from "../../../service/cody-wizard/context/policy-context";
import {fqcn} from "../../../service/cody-wizard/node/fqcn";
import {Rule} from "../../../service/cody-wizard/rule-engine/configuration";
import {RulesDependencyMetadata} from "../../../service/cody-wizard/rule-engine/types";
import EditorToolbar from "./EditorToolbar";
import {getPossibleStateRefs} from "./Typeahead/elements-tree-lookup";
import {makePolicyRulesSchema} from "./Validation/policy-rules-schema";
import {setSideBySideCell, toggleSidebarEditor} from "../../../../Layout/actions/commands";
import {Schema} from "../../../service/cody-wizard/schema/schema";
import {getPossibleRefs} from "./SchemaEditor";
import {getRefVo} from "../../../service/cody-wizard/vo/get-ref-vo";


interface OwnProps {
  boardId: BoardId;
  elementId: string;
  eventSchema: Schema;
  rules: Rule[];
  policyTargets: List<Node>;
  dependencies: Record<string, RulesDependencyMetadata>;
  onRulesChanged: (rules: Rule[] | undefined) => void;
  autofocus?: boolean;
  ctx: ImmutablePolicyContext;
  readonly?: boolean;
  sessionId?: string;
}

type PolicyRulesEditorProps = OwnProps & WithNamespaces;

const unsavedSessions: Record<string, string> = {};
let isRefOpening = false;

const PolicyRulesEditor = (props: PolicyRulesEditorProps) => {
  const elementsTree = useElementsTree(props.boardId);
  const codeEditorRef = useRef<CodeEditor|null>(null);
  const [editorHasFocus, setEditorHasFocus] = useState(false);
  const [invalidJson, setInvalidJson] = useState(false);
  const editorState = useSelector(makeEditorStateByNameSelector('policy-rules'));
  const dispatch = useDispatch();

  const possibleServices: string[] = [];

  for (const dependenciesKey in props.dependencies) {
    if(props.dependencies[dependenciesKey].type === "service") {
      possibleServices.push(props.dependencies[dependenciesKey].alias || dependenciesKey);
    }
  }

  let value = stringify(props.rules, null, 2);

  useEffect(() => {
    if (!codeEditorRef.current) {
      return;
    }

    let isInvalidJson = false;

    if(props.sessionId && typeof unsavedSessions[props.sessionId] === "string") {
      value = unsavedSessions[props.sessionId];
      isInvalidJson = true;
    }

    codeEditorRef.current!.initializeModel({
      fileId: `${props.sessionId}-policy-rules.json`,
      language: "json",
      value,
      schema: makePolicyRulesSchema(
        props.policyTargets.filter(t => t.getType() === NodeType.command).map(c => fqcn(c)).toArray(),
        possibleServices,
        getPossibleStateRefs(elementsTree),
        getPossibleRefs(elementsTree),
        getExprSuggests(props.eventSchema)
      ),
    });

    setInvalidJson(isInvalidJson);
  }, [props.elementId, value, props.policyTargets, props.dependencies, props.eventSchema.toString()]);

  useEffect(() => {
    // This is needed to prevent wrong schemaChanged events due to onBlur firing when ref is loaded
    if(isRefOpening) {
      window.setTimeout(() => {
        isRefOpening = false;
      }, 500);
    }
  }, [props.sessionId]);

  useEffect(() => {
    if(props.autofocus && codeEditorRef.current) {
      codeEditorRef.current.focus();
    }
  }, [props.autofocus])

  const propagateChanges = (editorStr: string, silentError?: boolean): boolean => {
    if(editorStr === '') {
      props.onRulesChanged(undefined);

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }
      return true;
    }

    try {
      const changedRules = parse(editorStr);
      props.onRulesChanged(changedRules);

      if(props.sessionId && unsavedSessions[props.sessionId]) {
        delete unsavedSessions[props.sessionId];
      }

      return true;
    } catch (e) {
      console.error("[CodyWizard] Business Rules Editor invalid JSON: ", e);
      if(!silentError) {
        setInvalidJson(true);
      }
      return false;
    }
  }

  const handleOpenInformationReference = (ref: string) => {
    const refVo = getRefVo(Schema.fromString(ref), elementsTree);

    if(refVo) {
      isRefOpening = true;
      dispatch(setSideBySideCell(refVo.getId()));
    }
  }

  return <FormField>
    {invalidJson && <Message error={true} size="small" content={props.t('insp.cody_wizard.schema_editor.invalid_json') as string} icon="warning" style={{display: 'flex'}}/>}

    <EditorToolbar expanded={editorState.expanded} double={editorState.double} onExpandChanged={(expand, double) => dispatch(toggleSidebarEditor('policy-rules', expand, !!double))}/>
    <CodeEditor
      containerId={`cody-policy-rules-editor-${props.sessionId}`}
      ref={codeEditorRef}
      onOpenInformationReference={handleOpenInformationReference}
      repairJson={true}
      options={{
        fontSize: 12,
        folding: true,
        glyphMargin: false,
        lineNumbers: true,
        lineDecorationsWidth: 3,
        minimap: {
          enabled: false
        },
        formatOnPaste: true,
        scrollBeyondLastLine: false,
        mouseWheelZoom: true,
        automaticLayout: true,
        scrollbar: {
          alwaysConsumeMouseWheel: false
        },
        readOnly: props.readonly,
        fixedOverflowWidgets: true,
      }}
      className={"code editor" + (editorHasFocus ? ' focus' : '') + (editorState.expanded ? ' expanded' : '')  + (editorState.double ? ' double' : '')}
      onBlur={() => {
        if(codeEditorRef.current) {
          const changedRulesStr = codeEditorRef.current.retrievePayload();

          console.log("[CodyWizard] editor rules", changedRulesStr);

          const validJson = propagateChanges(changedRulesStr);

          setEditorHasFocus(false);

          if(!validJson && props.sessionId) {
            unsavedSessions[props.sessionId] = changedRulesStr;
          }
        }
      }}
      onFocus={() => {
        setEditorHasFocus(true);
      }}
    />
  </FormField>
};

export default withNamespaces()(PolicyRulesEditor);


const getExprSuggests = (eventSchema: Schema): string[] => {
  const suggests: string[] = eventSchema.getObjectProperties().map(prop => `event.${prop}`);

  suggests.push('meta.user.userId');

  return suggests;
}
